import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useAttachRecord } from '@/hooks/use-clause-status';
import { usePlatformModules } from '@/hooks/use-modules';
import { getModuleDisplayName } from '@/lib/modules';
import { Card, CardContent } from '@/app/components/ui/card';
import type {
  Asset,
  Inspection,
  JSA,
  SafeWorkPlan,
  RescuePlan,
  IncidentReport,
  ToolboxTalk,
  TechCertification,
  ControlledDocument,
  NominatedPerson,
  InsurancePolicy,
  ComplianceIncidentReportListItem,
  FiledDocument,
} from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

type SourceModelKey =
  | 'asset'
  | 'inspection'
  | 'jsa'
  | 'safeworkplan'
  | 'rescueplan'
  | 'incidentreport'
  | 'toolboxtalk'
  | 'techcertification'
  | 'controlleddocument'
  | 'nominatedperson'
  | 'insurancepolicy'
  | 'complianceincidentreport'
  | 'fileddocument';

interface SourceModelOption {
  key: SourceModelKey;
  sourceModule: string;
  sourceModelLabel: string;
  endpoint: string;
  getLabel: (record: unknown) => string;
}

function assetLabel(record: unknown): string {
  const r = record as Asset;
  const composed = `${r.manufacturer ?? ''} ${r.model ?? ''}`.trim();
  return r.display_name || composed || `Asset ${r.id}`;
}

function inspectionLabel(record: unknown): string {
  const r = record as Inspection & { asset_name?: string };
  const asset = r.asset_name || `Asset ${r.asset ?? 'unknown'}`;
  const date = formatDateOnly(r.date, '');
  return `${asset}${date ? ` — ${date}` : ''}`.trim();
}

function jsaLabel(record: unknown): string {
  return (record as JSA).title || `JSA ${(record as { id: number }).id}`;
}

function safeWorkPlanLabel(record: unknown): string {
  return (record as SafeWorkPlan).title || `Safe Work Plan ${(record as { id: number }).id}`;
}

function rescuePlanLabel(record: unknown): string {
  return (record as RescuePlan).title || `Rescue Plan ${(record as { id: number }).id}`;
}

function incidentReportLabel(record: unknown): string {
  return (record as IncidentReport).title || `Incident ${(record as { id: number }).id}`;
}

function toolboxTalkLabel(record: unknown): string {
  return (record as ToolboxTalk).title || `Toolbox Talk ${(record as { id: number }).id}`;
}

function techCertificationLabel(record: unknown): string {
  const r = record as TechCertification;
  const tech = r.tech_name || `Tech ${r.tech ?? 'unknown'}`;
  const cert = r.cert_name || 'Certification';
  return `${tech} — ${cert}`;
}

function controlledDocumentLabel(record: unknown): string {
  return (record as ControlledDocument).title || `Document ${(record as { id: number }).id}`;
}

function nominatedPersonLabel(record: unknown): string {
  const r = record as NominatedPerson;
  return `${r.name || 'Unnamed'}${r.role_display ? ` (${r.role_display})` : ''}`;
}

function insurancePolicyLabel(record: unknown): string {
  return (record as InsurancePolicy).policy_name || `Policy ${(record as { id: number }).id}`;
}

function complianceIncidentLabel(record: unknown): string {
  const r = record as ComplianceIncidentReportListItem;
  const number = r.incident_number || `Incident ${r.id}`;
  const title = r.title;
  return title ? `${number} — ${title}` : number;
}

function filedDocumentLabel(record: unknown): string {
  const r = record as FiledDocument;
  return r.title || `Filed Document ${r.id}`;
}

const SOURCE_MODEL_OPTIONS: SourceModelOption[] = [
  {
    key: 'asset',
    sourceModule: 'inventory',
    sourceModelLabel: 'Asset',
    endpoint: '/api/v1/assets/',
    getLabel: assetLabel,
  },
  {
    key: 'inspection',
    sourceModule: 'inventory',
    sourceModelLabel: 'Inspection',
    endpoint: '/api/v1/inspections/',
    getLabel: inspectionLabel,
  },
  {
    key: 'jsa',
    sourceModule: 'safety',
    sourceModelLabel: 'JSA',
    endpoint: '/api/v1/safety/jsas/',
    getLabel: jsaLabel,
  },
  {
    key: 'safeworkplan',
    sourceModule: 'safety',
    sourceModelLabel: 'Safe Work Plan',
    endpoint: '/api/v1/safety/work-plans/',
    getLabel: safeWorkPlanLabel,
  },
  {
    key: 'rescueplan',
    sourceModule: 'safety',
    sourceModelLabel: 'Rescue Plan',
    endpoint: '/api/v1/safety/rescue-plans/',
    getLabel: rescuePlanLabel,
  },
  {
    key: 'incidentreport',
    sourceModule: 'safety',
    sourceModelLabel: 'Incident Report',
    endpoint: '/api/v1/safety/incidents/',
    getLabel: incidentReportLabel,
  },
  {
    key: 'toolboxtalk',
    sourceModule: 'safety',
    sourceModelLabel: 'Toolbox Talk',
    endpoint: '/api/v1/safety/toolbox-talks/',
    getLabel: toolboxTalkLabel,
  },
  {
    key: 'techcertification',
    sourceModule: 'crew',
    sourceModelLabel: 'Tech Certification',
    endpoint: '/api/v1/crew/certifications/',
    getLabel: techCertificationLabel,
  },
  {
    key: 'controlleddocument',
    sourceModule: 'compliance',
    sourceModelLabel: 'Controlled Document',
    endpoint: '/api/v1/compliance/documents/',
    getLabel: controlledDocumentLabel,
  },
  {
    key: 'nominatedperson',
    sourceModule: 'compliance',
    sourceModelLabel: 'Nominated Person',
    endpoint: '/api/v1/compliance/personnel/',
    getLabel: nominatedPersonLabel,
  },
  {
    key: 'insurancepolicy',
    sourceModule: 'compliance',
    sourceModelLabel: 'Insurance Policy',
    endpoint: '/api/v1/compliance/insurance/',
    getLabel: insurancePolicyLabel,
  },
  {
    key: 'complianceincidentreport',
    sourceModule: 'compliance',
    sourceModelLabel: 'Compliance Incident',
    endpoint: '/api/v1/compliance/incidents/',
    getLabel: complianceIncidentLabel,
  },
  {
    key: 'fileddocument',
    sourceModule: 'core',
    sourceModelLabel: 'Filed Document',
    endpoint: '/api/v1/filed-documents/',
    getLabel: filedDocumentLabel,
  },
];

const SOURCE_MODEL_CONFIG: Record<SourceModelKey, SourceModelOption> =
  SOURCE_MODEL_OPTIONS.reduce((acc, opt) => {
    acc[opt.key] = opt;
    return acc;
  }, {} as Record<SourceModelKey, SourceModelOption>);

interface AttachableRecord {
  id: number;
  label: string;
}

function extractRecords(responseData: unknown): unknown[] {
  if (
    responseData &&
    typeof responseData === 'object' &&
    'results' in responseData &&
    Array.isArray((responseData as { results: unknown }).results)
  ) {
    return (responseData as { results: unknown[] }).results;
  }
  if (Array.isArray(responseData)) {
    return responseData;
  }
  return [];
}

function useAttachableRecords(selectedSourceModel: SourceModelKey | '') {
  const config = selectedSourceModel ? SOURCE_MODEL_CONFIG[selectedSourceModel] : null;

  return useQuery({
    queryKey: ['attachable-records', selectedSourceModel],
    queryFn: async (): Promise<{ records: AttachableRecord[]; hasMore: boolean }> => {
      if (!config) return { records: [], hasMore: false };
      const response = await apiClient.get<unknown>(config.endpoint, {
        params: { page_size: 100 },
      });
      const rawRecords = extractRecords(response.data);
      const records = rawRecords
        .filter(
          (record): record is { id: number } =>
            record !== null &&
            typeof record === 'object' &&
            typeof (record as { id?: unknown }).id === 'number',
        )
        .map((record) => ({
          id: record.id,
          label: config.getLabel(record),
        }));
      return { records, hasMore: rawRecords.length === 100 };
    },
    enabled: !!selectedSourceModel,
  });
}

export function AttachRecordPicker({
  clauseStatusId,
  isOpen,
  onClose,
}: {
  clauseStatusId: number;
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    isModuleEnabled,
    isLoading: modulesLoading,
    error: modulesError,
    modules,
  } = usePlatformModules();
  const [selectedSourceModel, setSelectedSourceModel] = useState<SourceModelKey | ''>('');
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading, isError, error } = useAttachableRecords(selectedSourceModel);
  const attachRecord = useAttachRecord(clauseStatusId);

  const records = data?.records ?? [];
  const filteredRecords = searchTerm.trim()
    ? records.filter((r) => r.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : records;
  const selectedRecord = filteredRecords.find((r) => String(r.id) === selectedRecordId);

  useEffect(() => {
    if (selectedRecordId && !filteredRecords.some((r) => String(r.id) === selectedRecordId)) {
      setSelectedRecordId('');
    }
  }, [filteredRecords, selectedRecordId]);

  if (!isOpen) return null;

  const isSourceModelEnabled = (key: SourceModelKey): boolean => {
    const config = SOURCE_MODEL_CONFIG[key];
    if (!config || config.sourceModule === 'core') return true;
    if (!modules || modulesError) return true;
    return isModuleEnabled(config.sourceModule);
  };

  const availableOptions = SOURCE_MODEL_OPTIONS.filter((opt) => isSourceModelEnabled(opt.key));

  const groupedOptions = availableOptions.reduce<Record<string, SourceModelOption[]>>((acc, opt) => {
    const moduleLabel =
      opt.sourceModule === 'core' ? 'General' : getModuleDisplayName(opt.sourceModule);
    acc[moduleLabel] = acc[moduleLabel] ?? [];
    acc[moduleLabel].push(opt);
    return acc;
  }, {});

  const selectedConfig = selectedSourceModel ? SOURCE_MODEL_CONFIG[selectedSourceModel] : null;
  const hasMore = data?.hasMore ?? false;

  const handleSourceModelChange = (value: string) => {
    setSelectedSourceModel(value as SourceModelKey);
    setSelectedRecordId('');
    setSearchTerm('');
  };

  const handleAttach = () => {
    if (!selectedConfig || !selectedRecordId || !selectedRecord) return;
    attachRecord.mutate(
      {
        source_module: selectedConfig.sourceModule,
        source_model: selectedConfig.key,
        source_id: Number(selectedRecordId),
        title: selectedRecord.label,
      },
      {
        onSuccess: () => {
          setSelectedSourceModel('');
          setSelectedRecordId('');
          setSearchTerm('');
          onClose();
        },
      },
    );
  };

  const handleClose = () => {
    setSelectedSourceModel('');
    setSelectedRecordId('');
    setSearchTerm('');
    onClose();
  };

  if (modulesLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 py-3">
          <p className="text-sm text-gray-500">Loading module settings…</p>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <Paperclip className="h-4 w-4" />
          Attach existing record
        </div>
        {modulesError && (
          <p className="text-xs text-amber-700">
            Could not load module settings. Some record types may be unavailable if their module is disabled.
          </p>
        )}
        <div data-field-container>
          <label htmlFor="attach-record-type" className="mb-1 block text-sm font-medium">
            Record type *
          </label>
          <select
            id="attach-record-type"
            value={selectedSourceModel}
            onChange={(event) => handleSourceModelChange(event.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            required
          >
            <option value="">Select a record type…</option>
            {Object.entries(groupedOptions).map(([moduleLabel, opts]) => (
              <optgroup key={moduleLabel} label={moduleLabel}>
                {opts.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.sourceModelLabel}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        {selectedSourceModel && !isLoading && !isError && hasMore && (
          <p className="text-xs text-amber-700">
            Showing the first 100 {selectedConfig?.sourceModelLabel.toLowerCase()} records loaded; search narrows the loaded list.
          </p>
        )}
        {isError && (
          <p className="text-sm text-red-600">
            Failed to load records: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        )}
        <input
          type="text"
          placeholder="Search records…"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          disabled={!selectedSourceModel || isLoading || isError}
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
        />
        <div data-field-container>
          <label htmlFor="attach-record" className="mb-1 block text-sm font-medium">
            Record *
          </label>
          <select
            id="attach-record"
            value={selectedRecordId}
            onChange={(event) => setSelectedRecordId(event.target.value)}
            disabled={!selectedSourceModel || isLoading || isError}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            required
          >
            <option value="">
              {isLoading ? 'Loading records…' : isError ? 'Error loading records' : selectedSourceModel ? 'Choose a record…' : 'Select a record type first'}
            </option>
            {filteredRecords.map((record) => (
              <option key={record.id} value={record.id}>
                {record.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleAttach}
            disabled={attachRecord.isPending}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {attachRecord.isPending ? 'Attaching…' : 'Attach'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
